import { useEffect, useRef, useState } from "react";
import { apiGet } from "../api";
import type { CardSearchHit } from "../types";
import { CardThumb } from "./CardImage";

type SingleProps = {
  multi?: false;
  value: CardSearchHit | null;
  onSelect: (card: CardSearchHit | null) => void;
  arenaOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
};

type MultiProps = {
  multi: true;
  values: CardSearchHit[];
  onAdd: (card: CardSearchHit) => void;
  onRemove: (oracleId: string) => void;
  arenaOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
  maxItems?: number;
};

type Props = SingleProps | MultiProps;

/**
 * Debounced card-name autocomplete. In single mode (default) fires onSelect
 * with the picked card and renders a preview chip. In `multi` mode it keeps
 * the input visible after each pick so the user can add more cards; the
 * parent owns the list of selected values.
 */
export function CardPicker(props: Props) {
  const { arenaOnly, placeholder = "Start typing a card name...", disabled } = props;
  const [input, setInput] = useState("");
  const [results, setResults] = useState<CardSearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const seqRef = useRef(0);

  const isMulti = props.multi === true;
  const selectedIds = new Set(
    isMulti ? props.values.map((v) => v.oracle_id) : [],
  );
  const atCap =
    isMulti &&
    props.maxItems != null &&
    props.values.length >= props.maxItems;

  useEffect(() => {
    if (!input.trim() || input.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const seq = ++seqRef.current;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: input.trim(), limit: "10" });
        if (arenaOnly) params.set("arena_only", "true");
        const data = await apiGet<CardSearchHit[]>(
          `/api/cards/search?${params.toString()}`,
        );
        if (seqRef.current !== seq) return;
        setResults(data);
        setOpen(true);
        setActiveIndex(data.length ? 0 : -1);
      } catch {
        if (seqRef.current !== seq) return;
        setResults([]);
      } finally {
        if (seqRef.current === seq) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [input, arenaOnly]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (card: CardSearchHit) => {
    if (isMulti) {
      if (atCap) return;
      if (!selectedIds.has(card.oracle_id)) {
        props.onAdd(card);
      }
      setInput("");
      setResults([]);
      setOpen(false);
      // Keep focus so the user can keep adding cards without clicking again.
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      props.onSelect(card);
      setInput("");
      setResults([]);
      setOpen(false);
    }
  };

  const clearSingle = () => {
    if (isMulti) return;
    props.onSelect(null);
    setInput("");
    setResults([]);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || results.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) pick(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Single-select mode: show a chip when a card is picked.
  if (!isMulti && props.value) {
    return (
      <div className="card-picker" ref={rootRef}>
        <div className="card-picker-selected">
          <CardThumb card={props.value} size="sm" />
          <div className="card-picker-selected-body">
            <div className="card-picker-selected-name">{props.value.name}</div>
            <div className="card-picker-selected-meta muted">
              {props.value.type_line}
              {props.value.mana_cost ? ` · ${props.value.mana_cost}` : ""}
            </div>
          </div>
          <button
            type="button"
            className="secondary small"
            onClick={clearSingle}
            disabled={disabled}
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // Multi-select mode: show the chips list above the input.
  const chips =
    isMulti && props.values.length > 0 ? (
      <ul className="card-picker-chips" aria-label="Selected cards">
        {props.values.map((c) => (
          <li key={c.oracle_id} className="card-picker-chip">
            <CardThumb card={c} size="sm" />
            <span className="card-picker-chip-name">{c.name}</span>
            <button
              type="button"
              aria-label={`Remove ${c.name}`}
              className="card-picker-chip-remove"
              onClick={() => props.onRemove(c.oracle_id)}
              disabled={disabled}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <div className="card-picker" ref={rootRef}>
      {chips}
      <div className="card-picker-input-wrap">
        <input
          ref={inputRef}
          type="text"
          placeholder={
            atCap && isMulti && props.maxItems
              ? `Maximum of ${props.maxItems} cards reached`
              : placeholder
          }
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => input && setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled || atCap}
          autoComplete="off"
          spellCheck={false}
        />
        {loading && <span className="card-picker-spinner" aria-hidden />}
        {open && results.length > 0 && (
          <ul className="card-picker-dropdown" role="listbox">
            {results.map((r, i) => {
              const already = selectedIds.has(r.oracle_id);
              return (
                <li
                  key={r.oracle_id}
                  role="option"
                  aria-selected={i === activeIndex}
                  className={[
                    i === activeIndex ? "active" : "",
                    already ? "disabled" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!already) pick(r);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  {r.image_url ? (
                    <img
                      src={r.image_url}
                      alt={`${r.name} card art`}
                      width={34}
                      className="card-picker-thumb"
                      loading="lazy"
                    />
                  ) : (
                    <span className="card-picker-thumb placeholder" />
                  )}
                  <span className="card-picker-name">
                    {r.name}
                    {already ? " (added)" : ""}
                  </span>
                  <span className="card-picker-meta">
                    {r.mana_cost ? `${r.mana_cost} · ` : ""}
                    {r.type_line}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        {open && !loading && input.trim().length >= 2 && results.length === 0 && (
          <div className="card-picker-empty">No cards found.</div>
        )}
      </div>
    </div>
  );
}
